import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import "../AdminDashboard/AdminDashboard.css";
import "./GymAssets.css";
import { JOB_TYPES, formatDate, hoursLabel, jobTypeLabel, money } from "./gymAssetOptions";

const emptyJob = () => ({
  job_type: "maintenance",
  title: "",
  details: "",
  cost: "",
  vendor: "",
  started_at: new Date().toISOString().slice(0, 16),
  ended_at: "",
});

export default function GymMaintenance() {
  const [params] = useSearchParams();
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ q: "", branch_id: "", job_type: "", status: "", from: "", to: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assetQuery, setAssetQuery] = useState("");
  const [choices, setChoices] = useState([]);
  const [asset, setAsset] = useState(null);
  const [job, setJob] = useState(emptyJob());
  const [saving, setSaving] = useState(false);

  const load = async (next = filters) => {
    setLoading(true);
    try {
      const res = await UserServices.gymMaintenanceReport(next);
      setReport(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load maintenance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    UserServices.branchNameAll().then((res) => setBranches(res.data?.data || [])).catch(() => setBranches([]));
    load(filters);
    const assetId = params.get("asset");
    if (assetId) {
      UserServices.gymAsset(assetId).then((res) => setAsset(res.data?.data || null)).catch(() => setAsset(null));
    }
  }, []);

  useEffect(() => {
    const query = assetQuery.trim();
    if (query.length < 1) return undefined;
    const timer = setTimeout(() => {
      UserServices.gymAssets({ q: query })
        .then((res) => setChoices(res.data?.data || []))
        .catch(() => setChoices([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [assetQuery]);

  const apply = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    load(next);
  };

  const saveJob = async (event) => {
    event.preventDefault();
    if (!asset?.id) {
      toast.error("Select the asset by name or serial");
      return;
    }
    if (!job.title.trim()) {
      toast.error("Give the work a short title");
      return;
    }
    setSaving(true);
    try {
      await UserServices.addGymAssetJob(asset.id, {
        ...job,
        cost: job.cost === "" ? 0 : Number(job.cost),
        started_at: job.started_at.replace("T", " "),
        ended_at: job.ended_at ? job.ended_at.replace("T", " ") : null,
        status: job.ended_at ? "done" : "open",
      });
      toast.success("Work saved against " + (asset.serial_number || asset.asset_code));
      setJob(emptyJob());
      await load(filters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save the work");
    } finally {
      setSaving(false);
    }
  };

  const closeJob = async (row) => {
    try {
      await UserServices.updateGymAssetJob(row.id, {
        ...row,
        status: "done",
        ended_at: new Date().toISOString().slice(0, 19).replace("T", " "),
      });
      toast.success("Work closed");
      await load(filters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not close the work");
    }
  };

  const totals = report?.totals || {};

  return (
    <div className="wrapper gym-assets">
      <ToastContainer />
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ad-topbar d-flex justify-content-between align-items-start gap-3">
              <div>
                <p className="ad-eyebrow">Facility</p>
                <h2 className="ad-title">Maintenance</h2>
                <p className="ad-subtitle">Pick one machine by serial, then record maintenance, a repair, downtime, or an inspection.</p>
              </div>
              <div className="gym-actions">
                <Link className="btn btn-outline-secondary" to="/gym-assets">Assets</Link>
                <Link className="btn btn-outline-secondary" to="/facility-dashboard">Facility dashboard</Link>
              </div>
            </div>

            <div className="gym-kpis mb-3">
              <div className="gym-kpi"><span>Jobs</span><strong>{totals.jobs ?? "—"}</strong></div>
              <div className="gym-kpi is-alert"><span>Open</span><strong>{totals.open ?? "—"}</strong></div>
              <div className="gym-kpi"><span>Cost</span><strong>{report ? money(totals.cost) : "—"}</strong></div>
              <div className="gym-kpi"><span>Downtime</span><strong>{report ? hoursLabel(totals.downtime_minutes) : "—"}</strong></div>
            </div>

            <div className="gym-split mb-3">
              <section className="gym-panel p-3">
                <div className="gym-panel-head">
                  <div>
                    <h3>Record work</h3>
                    <p>Search the serial or asset code, then save the job.</p>
                  </div>
                </div>
                <form className="gym-form-grid" onSubmit={saveJob}>
                  <label className="gym-field gym-span-2">Asset
                    <input className="form-control" placeholder="Name, serial, or code" value={assetQuery} onChange={(event) => setAssetQuery(event.target.value)} />
                    <select className="form-select" value={asset?.id || ""} onChange={(event) => {
                      const picked = [asset, ...choices].filter(Boolean).find((row) => String(row.id) === event.target.value);
                      setAsset(picked || null);
                    }}>
                      <option value="">Select the asset</option>
                      {[asset, ...choices].filter((row, index, list) => row && list.findIndex((item) => item && item.id === row.id) === index).map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name} · {row.serial_number || "No serial"} · {row.asset_code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="gym-field">Type
                    <select className="form-select" value={job.job_type} onChange={(event) => setJob({ ...job, job_type: event.target.value })}>
                      {JOB_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <label className="gym-field">Title
                    <input className="form-control" value={job.title} onChange={(event) => setJob({ ...job, title: event.target.value })} />
                  </label>
                  <label className="gym-field">Cost (PKR)
                    <input className="form-control" type="number" min="0" value={job.cost} onChange={(event) => setJob({ ...job, cost: event.target.value })} />
                  </label>
                  <label className="gym-field">Vendor
                    <input className="form-control" value={job.vendor} onChange={(event) => setJob({ ...job, vendor: event.target.value })} />
                  </label>
                  <label className="gym-field">Started
                    <input className="form-control" type="datetime-local" value={job.started_at} onChange={(event) => setJob({ ...job, started_at: event.target.value })} />
                  </label>
                  <label className="gym-field">Ended
                    <input className="form-control" type="datetime-local" value={job.ended_at} onChange={(event) => setJob({ ...job, ended_at: event.target.value })} />
                  </label>
                  <label className="gym-field gym-span-2">What was done
                    <textarea className="form-control" rows="2" value={job.details} onChange={(event) => setJob({ ...job, details: event.target.value })} />
                  </label>
                  <div><button className="btn btn-danger" disabled={saving} type="submit">{saving ? "Saving..." : "Save work"}</button></div>
                </form>
              </section>

              <section className="gym-panel p-3">
                <div className="gym-panel-head"><h3>Report</h3></div>
                {(report?.by_type || []).length === 0 ? <p className="gym-muted mb-0">No work in this filter.</p> : (
                  <table className="table gym-table mb-3">
                    <thead><tr><th>Type</th><th>Jobs</th><th>Open</th><th>Cost</th><th>Downtime</th></tr></thead>
                    <tbody>
                      {report.by_type.map((row) => (
                        <tr key={row.job_type}>
                          <td>{jobTypeLabel(row.job_type)}</td>
                          <td>{row.jobs}</td>
                          <td>{row.open}</td>
                          <td>{money(row.cost)}</td>
                          <td>{hoursLabel(row.downtime_minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(report?.by_branch || []).length > 0 ? (
                  <table className="table gym-table mb-0">
                    <thead><tr><th>Branch</th><th>Jobs</th><th>Cost</th><th>Downtime</th></tr></thead>
                    <tbody>
                      {report.by_branch.map((row) => (
                        <tr key={row.branch_id}>
                          <td>{row.branch_name || "Branch"}</td>
                          <td>{row.jobs}</td>
                          <td>{money(row.cost)}</td>
                          <td>{hoursLabel(row.downtime_minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
              </section>
            </div>

            <section className="gym-panel p-3">
              <div className="gym-filters mb-3">
                <input className="form-control" placeholder="Search asset, serial, title, or vendor" value={filters.q} onChange={(event) => apply({ q: event.target.value })} />
                <select className="form-select" value={filters.branch_id} onChange={(event) => apply({ branch_id: event.target.value })}>
                  <option value="">All branches</option>
                  {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                </select>
                <select className="form-select" value={filters.job_type} onChange={(event) => apply({ job_type: event.target.value })}>
                  <option value="">All types</option>
                  {JOB_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div className="gym-filters">
                <select className="form-select" value={filters.status} onChange={(event) => apply({ status: event.target.value })}>
                  <option value="">Open and closed</option>
                  <option value="open">Open</option>
                  <option value="done">Closed</option>
                </select>
                <input className="form-control" type="date" value={filters.from} onChange={(event) => apply({ from: event.target.value })} />
                <input className="form-control" type="date" value={filters.to} onChange={(event) => apply({ to: event.target.value })} />
              </div>
              <div className="table-responsive mt-3">
                <table className="table gym-table mb-0">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Work</th>
                      <th>When</th>
                      <th>Cost</th>
                      <th>Downtime</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <tr><td colSpan="7">Loading maintenance...</td></tr> : (report?.jobs || []).length === 0 ? (
                      <tr><td colSpan="7">No maintenance recorded for this filter.</td></tr>
                    ) : report.jobs.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <Link className="gym-asset-name" to={`/gym-assets/${row.asset_id}`}>{row.asset_name}</Link>
                          <div className="gym-asset-meta">{row.serial_number || "No serial"} · {row.asset_code}</div>
                        </td>
                        <td>
                          <div>{row.title}</div>
                          <div className="gym-asset-meta">{jobTypeLabel(row.job_type)}{row.vendor ? ` · ${row.vendor}` : ""}</div>
                        </td>
                        <td>{formatDate(row.started_at)}</td>
                        <td>{money(row.cost)}</td>
                        <td>{hoursLabel(row.downtime_minutes)}</td>
                        <td><span className={`gym-pill ${row.status === "open" ? "is-under_repair" : "is-active"}`}>{row.status === "open" ? "Open" : "Closed"}</span></td>
                        <td>{row.status === "open" ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => closeJob(row)}>Close</button> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
